import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  IconButton,
  Divider,
  Avatar,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import { useDispatch, useSelector } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import axios from "axios";
import { AppDispatch, RootState } from "src/store";
import {
  ProfileFormInputs,
  updateProfile,
  hydrateUser,
  getUserDisplayName,
  getUserEmail,
} from "src/features/authSlice";
import { API_URL } from "src/config/api";
import { useTranslation } from "react-i18next";

interface EditableFieldConfig {
  label: string;
  value: keyof ProfileFormInputs;
}

const ProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { user, token, loading } = useSelector((state: RootState) => state.auth);

  const { control, handleSubmit, setValue, reset } = useForm<ProfileFormInputs>({
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      position: "",
      workEmail: "",
    },
  });

  const [selectedAvatar, setSelectedAvatar] = useState<File | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string>("");
  const [editingField, setEditingField] = useState<keyof ProfileFormInputs | null>(null);

  const editableFields: EditableFieldConfig[] = [
    { label: t("settings.profile.firstName"), value: "firstName" },
    { label: t("settings.profile.lastName"), value: "lastName" },
    { label: t("settings.profile.workEmail"), value: "email" },
  ];

  useEffect(() => {
    if (!user) {
      dispatch(hydrateUser());
    }
  }, [dispatch, user]);

  useEffect(() => {
    let objectUrl = "";

    const fetchAvatar = async () => {
      if (user?.accountType === "company" && (user as any).avatarId && token) {
        try {
          const response = await axios.get(`${API_URL}/company/auth/getavatar`, {
            headers: { Authorization: `Bearer ${token}` },
            responseType: "blob",
          });
          objectUrl = URL.createObjectURL(response.data);
          setAvatarUrl(objectUrl);
        } catch (error) {
          console.error("Failed to fetch avatar:", error);
          setAvatarUrl("");
        }
      } else {
        setAvatarUrl("");
      }
    };

    fetchAvatar();

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [user, token]);

  useEffect(() => {
    if (!user) return;

    const displayName = getUserDisplayName(user);
    const nameParts = displayName.split(" ");
    const firstName = nameParts[0] || "";
    const lastName = nameParts.slice(1).join(" ") || "";
    const email = getUserEmail(user);

    reset({
      firstName,
      lastName,
      email,
      workEmail: email,
      phone: "",
      position: "",
    });
  }, [user, reset]);

  const handleAvatarChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files?.[0] && token) {
      const file = event.target.files[0];

      if (file.size > 4 * 1024 * 1024) {
        alert(t("settings.profile.errors.fileSize"));
        return;
      }

      const formData = new FormData();
      formData.append("avatar", file);

      try {
        await axios.post(`${API_URL}/company/auth/uploadavatar`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });

        setSelectedAvatar(file);
        dispatch(hydrateUser());

        const response = await axios.get(`${API_URL}/company/auth/getavatar`, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        });
        const url = URL.createObjectURL(response.data);
        setAvatarUrl(url);
      } catch (error: any) {
        console.error("Avatar upload failed:", error);
        alert(error.response?.data?.message || t("settings.profile.errors.uploadFailed"));
      }
    }
  };

  const handleEditClick = (field: keyof ProfileFormInputs) => {
    if (!user) return;

    const displayName = getUserDisplayName(user);
    const nameParts = displayName.split(" ");
    const firstName = nameParts[0] || "";
    const lastName = nameParts.slice(1).join(" ") || "";
    const email = getUserEmail(user);

    if (field === "firstName") {
      setValue("firstName", firstName);
    } else if (field === "lastName") {
      setValue("lastName", lastName);
    } else if (field === "email") {
      setValue("email", email);
    }

    setEditingField(field);
  };

  const handleSaveClick = async (data: ProfileFormInputs) => {
    const result = await dispatch(updateProfile(data));

    if (updateProfile.fulfilled.match(result)) {
      setEditingField(null);
    }
  };

  const getDisplayValue = (field: keyof ProfileFormInputs): string => {
    if (!user) return "";

    const displayName = getUserDisplayName(user);
    const nameParts = displayName.split(" ");
    const firstName = nameParts[0] || "";
    const lastName = nameParts.slice(1).join(" ") || "";

    if (field === "firstName") return firstName;
    if (field === "lastName") return lastName;
    if (field === "email") return getUserEmail(user);

    return "";
  };

  return (
    <Box
      sx={{
        margin: "auto",
        padding: 4,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography variant="h4" component="h1" gutterBottom>
        {t("settings.profile.title")}
      </Typography>

      <Grid container spacing={2} sx={{ mt: 2, alignItems: "center" }}>
        <Grid item xs={10}>
          <Divider sx={{ mb: 2 }} />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 3,
            }}
          >
            <Typography variant="body1" sx={{ color: "gray" }}>
              {t("settings.profile.profilePicture")}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Avatar
                sx={{ width: 70, height: 70 }}
                src={selectedAvatar ? URL.createObjectURL(selectedAvatar) : avatarUrl}
              >
                {!avatarUrl && !selectedAvatar && getUserDisplayName(user).charAt(0)}
              </Avatar>
              <IconButton
                color="primary"
                aria-label="upload picture"
                component="label"
                sx={{ ml: 2 }}
              >
                <input hidden accept="image/*" type="file" onChange={handleAvatarChange} />
                <PhotoCamera />
              </IconButton>
            </Box>
          </Box>
          <Divider />
        </Grid>

        {editableFields.map((field) => (
          <Grid item xs={10} key={field.value}>
            {editingField === field.value ? (
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <Controller
                  name={field.value}
                  control={control}
                  render={({ field: controllerField }) => (
                    <TextField
                      {...controllerField}
                      label={field.label}
                      variant="outlined"
                      fullWidth
                    />
                  )}
                />
                <Button
                  onClick={handleSubmit(handleSaveClick)}
                  variant="contained"
                  color="primary"
                  sx={{ ml: 2 }}
                  disabled={loading}
                >
                  {t("settings.profile.save")}
                </Button>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Typography variant="body1" sx={{ color: "gray" }}>
                  {field.label}
                </Typography>
                <Typography variant="body1">{getDisplayValue(field.value)}</Typography>
                <IconButton edge="end" aria-label="edit" onClick={() => handleEditClick(field.value)}>
                  <EditIcon color="primary" />
                </IconButton>
              </Box>
            )}
            <Divider />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default ProfilePage;
